import clone from 'lodash/clone';

import {
  ActionTypes,
  EventTypes,
  SUBSTORY_COLOR_LIST,
  CONTEXT_CLEAR_NODE,
  END_OF_QUERY,
} from 'constants/MogamboConstants';

import { register } from '@src/dispatcher';
import { getBlockData } from '@src/containers/SubStories/Graph/utils';

import createStore from './StoreUtils.js';

/**
 * Graph Store
 * All the store values that are related to the main graph
 * which also represents the list of substories inside a particular story of a bot
 * @type {[type]}
 */
const GraphStore = createStore({
  /**
   * _graph: array of substories, along with all the requisite node info to render the graph
   * _indexedColors: object (reference array) with all the colors indexed by node Id
   * nodeList: array of all nodes present across all the different substories
   * @type {[type]}
   */
  _graph: [],
  _graphLoading: true,
  _indexedColors: {},
  nodeList: [],
  graphStepsSelection: null,
  selectedSteps: {},

  /**
   * Adds a new node to the graph
   * We create a new node with some default values and a randomized id
   * and push it into the graph into the selected substory's list of steps
   * @param {[type]}   x        [description]
   * @param {[type]}   y        [description]
   * @param {[type]}   meta     [description]
   * @param {Function} callback [description]
   */
  addNewNode(x, y, meta, callback) {
    const currentSubstory = this.current_substory_id
      ? this._graph.filter((item) => item.id === this.current_substory_id)[0]
      : null;
    const color = currentSubstory ? currentSubstory.color : null;
    this._graph = this._graph.map((subStory) => {
      if (subStory.id === (meta?.subStory?.id || meta.sub_story_id)) {
        const id =
          meta?.block_id || `new${Math.random().toString(36).substring(6)}`;

        const newStep = {
          newNode: true,
          id,
          x,
          y,
          step_type: {
            start: meta?.is_start || false,
            [CONTEXT_CLEAR_NODE.KEY]: false,
          },
          title: meta?.title || '',
          active: true,
          source_list: [],
          destination_list: [],
          color,
          type: meta.step.type,
          [END_OF_QUERY.KEY]: meta?.data?.query_end || false,
        };
        subStory = clone(subStory);
        subStory.nodeList = clone(subStory.nodeList);
        subStory.nodeList.push(newStep);
        this.nodeList = this.nodeList.concat([newStep]);
        if (callback) {
          setTimeout(() => callback(id));
        }
      }

      return subStory;
    });
  },
  /**
   * Generates the graph based on the data fetched by the API
   * All the colors of the nodes are assigned here, the nodeList/_indexedColors keys are also
   * populated here when we iterate through the data
   * @param {[type]}   data       [description]
   * @param {[type]}   subStoryId [description]
   * @param {Function} callback   [description]
   */
  setGraph(graph, subStoryId, subStories, callback) {
    this._subStories = subStories.map((subStory, index) => ({
      ...subStory,
      color: SUBSTORY_COLOR_LIST[index % SUBSTORY_COLOR_LIST.length],
    }));

    let blocks = graph.blocks.map((block) => ({
      ...block,
      source_list: [],
      destination_list: [],
    }));

    graph.connections.forEach((connection) => {
      blocks = blocks.map((block) => {
        if (block.block_id === connection.source_block_id) {
          block.destination_list.push(connection.destination_block_id);
        }
        if (block.block_id === connection.destination_block_id) {
          block.source_list.push(connection.source_block_id);
        }
        return block;
      });
    });

    this.nodeList = [];

    this._graph = this._subStories.map((subStory) => {
      const nodeList = blocks.reduce((acc, block) => {
        if (subStory.id === block.sub_story_id) {
          acc = [...acc, getBlockData(block, subStory.color)];
        }
        return acc;
      }, []);
      this.nodeList = [...this.nodeList, ...nodeList];
      return {
        ...subStory,
        nodeList,
      };
    });

    this._graphLoading = false;
    if (callback) setTimeout(callback, 0);
  },

  createStep(newStep) {
    this._graph = this._graph.map((subStory, index) => {
      if (subStory.nodeList) {
        subStory.nodeList.map((step) => {
          if (step.id.startsWith('new')) {
            step.color =
              SUBSTORY_COLOR_LIST[index % SUBSTORY_COLOR_LIST.length];
            step.substory_id = newStep.substory_id;
            step.subStoryId = newStep.substory_id;
            step.id = newStep.id;
            step.title = newStep.title;
            step.step_type = newStep.step_type;
            step.domain_name = newStep.domain_name;
            step.system_name = newStep.system_name;
            this._indexedColors[step.id] = step.color;
            this._tempStep = step;
          }
        });
      }
      return subStory;
    });

    this.nodeList = this.nodeList.map((step) => {
      if (step.id.startsWith('new')) {
        return this._tempStep;
      }
      return step;
    });
  },

  removeNewSteps() {
    this._graph = this._graph.map((subStory) => {
      if (subStory.nodeList) {
        subStory.nodeList = subStory.nodeList.filter(
          (step) => !step.id.startsWith('new')
        );
      }
      return subStory;
    });

    this.nodeList = this.nodeList.filter((step) => !step.id.startsWith('new'));
  },

  /**
   * Get graph list
   * @return {[type]} [description]
   */
  getGraph() {
    return this._graph;
  },

  /**
   * Get sub story list
   * @return {[type]} [description]
   */
  getSubStories() {
    return this._subStories;
  },

  /**
   * Get graph loading status
   * @return {[type]} [description]
   */
  getGraphLoading() {
    return this._graphLoading;
  },
  setCurrentSubstoryId(id) {
    this.current_substory_id = id;
  },
  getCurrentSubstoryId() {
    return this.current_substory_id;
  },
  getSelectedSubstory() {
    return this.current_substory_id
      ? this._graph.filter((item) => item.id === this.current_substory_id)[0]
      : undefined;
  },
  setDroppable(droppable) {
    this.droppable = droppable;
  },
  getDroppable() {
    return this.droppable;
  },

  /**
   * Clear the graph
   * @return {[type]} [description]
   */
  clearGraph() {
    this._graph = [];
    this._graphLoading = true;
  },

  /**
   * Get the color for a particular node ID
   * @param  {[type]} stepId [description]
   * @return {[type]}        [description]
   */
  getStepColor(stepId) {
    return this._indexedColors[stepId] || {};
  },

  /**
   * update node meta data with given identity
   * @param  {[string]} id [id of node]
   * @param  {[object]} data [meta data for node]
   */
  updateNode(id, data) {
    let subStoryId = null;
    this.nodeList = this.nodeList.map((node) => {
      if (id === node.id) {
        subStoryId = node.subStoryId;
        return {
          ...node,
          ...data,
        };
      }
      return node;
    });
    this._graph = this._graph.map((subStory) => {
      if (subStory.id === subStoryId) {
        const nodeList = this.nodeList.filter(
          (node) => node.subStoryId === subStory.id
        );
        return {
          ...subStory,
          nodeList,
        };
      }
      return subStory;
    });
  },

  /**
   * Get all steps
   * @return {[type]} [description]
   */
  getAllSteps() {
    return this.nodeList;
  },

  getGraphStepsSelection() {
    return this.graphStepsSelection;
  },

  setGraphStepsSelection(value) {
    this.graphStepsSelection = value;
  },

  getSelectedSteps() {
    return this.selectedSteps;
  },

  setSelectedSteps(value) {
    this.selectedSteps = value;
    GraphStore.emitChange();
  },

  addABNode(nodeInfo) {
    this._graph = this._graph.map((subStory) => {
      let sourceList = [];
      if (subStory.id === nodeInfo.subStoryId) {
        subStory.nodeList.map((node) => {
          if (node.id === nodeInfo.nodeId) {
            sourceList = node.source_list;
          }
          return node;
        });

        const newStep = {
          newNode: true,
          id: nodeInfo.id,
          x: nodeInfo.x,
          y: nodeInfo.y,
          step_type: {
            start: false,
            [CONTEXT_CLEAR_NODE.KEY]: false,
          },
          title: '',
          active: true,
          source_list: sourceList,
          destination_list: [nodeInfo.variantNodeId, nodeInfo.nodeId],
          color: null,
          type: nodeInfo.step.type,
          [END_OF_QUERY.KEY]: false,
        };
        subStory = clone(subStory);
        subStory.nodeList = clone(subStory.nodeList);
        subStory.nodeList.push(newStep);
        this.nodeList = this.nodeList.concat([newStep]);
      }
      return subStory;
    });
  },
});

/**
 * Listen to actions that are dispatched by the dispatcher and accordingly
 * call some store functions depending on the ActionType
 * @type {[type]}
 */
GraphStore.dispatchToken = register((action) => {
  const actionType = action.type;
  action = action.action;
  switch (actionType) {
    case ActionTypes.SET_DROPPABLE:
      GraphStore.setDroppable(action.droppable);
      GraphStore.emitChange();
      break;
    case ActionTypes.RECEIVE_SUBSTORY_GRAPH:
      GraphStore.setGraph(
        action.graph,
        action.subStoryId,
        action.sub_story,
        action.callback
      );
      GraphStore.emitChange();
      break;
    case ActionTypes.SET_CURRENT_SUBSTORY_ID:
      GraphStore.setCurrentSubstoryId(action.id);
      GraphStore.emitChange();
      break;
    case ActionTypes.UPDATE_STEP_POSITION:
      GraphStore.updateNode(action.position.id, {
        x: action.position.x,
        y: action.position.y,
      });
      GraphStore.emitChange();
      break;
    case ActionTypes.CLEAR_GRAPH:
    case ActionTypes.FETCH_ALL_STORIES:
    case ActionTypes.GET_ALL_BOTS:
      GraphStore.clearGraph();
      GraphStore.emitChange();
      break;

    case ActionTypes.ADD_NEW_NODE:
      GraphStore.addNewNode(
        action.data.x,
        action.data.y,
        action.data.meta,
        action.data.callback
      );
      GraphStore.emitChange();
      break;

    case ActionTypes.ADD_AB_NODE:
      GraphStore.addABNode(action.data.abNodeInfo);
      GraphStore.emitChange();
      break;

    case ActionTypes.STEP_CREATED:
      GraphStore.createStep(action.data);
      GraphStore.emitChange();
      if (action.callback)
        setTimeout(() => action.callback(false, action.data), 1000);
      break;

    case ActionTypes.SET_URL_METADATA:
      if (!action.metaTags.step.isNew) {
        GraphStore.removeNewSteps();
        GraphStore.emitChange();
      }
      setTimeout(() => {
        GraphStore.emit(EventTypes.UPDATE_GRAPH, action);
      }, 0);
      break;

    case ActionTypes.FORCE_UPDATE_GRAPH:
      setTimeout(() => {
        GraphStore.emit(EventTypes.UPDATE_GRAPH, action);
      }, 0);
      break;
    default:
  }
});

export default GraphStore;
